import styles from './ModalWithPortal.module.scss'

import React, { useContext, useEffect } from 'react'
import { createPortal } from 'react-dom'

import { useThemeStore } from 'Stores/ThemeStore'
import FocusTrap from 'UtilityComponents/FocusTrap'
import classNames from 'classnames'

import usePortal from 'Hooks/usePortal'

import Mask from 'Atoms/Mask'
import PaginationControl from 'Atoms/PaginationControl/PaginationControl'
import Panel from 'Atoms/Panel'
import Svg from 'Atoms/Svg'

export function Modal({
  id,
  children,
  isOpen,
  onClose,
  focusTrap = false,
  disableOutsideClick = false,
  showCloseButton = true,
  showPagination = false,
  onPaginationLeft,
  onPaginationRight,
  containerClassName,
  inverseTheme,
}) {
  const target = usePortal(id, 300)

  const theme = useThemeStore((x) => x?.theme)

  const isInverseTheme = theme === 'dark' || inverseTheme

  const handleEscape = (event) => {
    if (event.keyCode === 27) onClose()
  }

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.top = `-${scrollY}px`
    } else {
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      window.scrollTo(0, parseInt(scrollY || '0') * -1)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) document.addEventListener('keydown', handleEscape, false)

    return () => {
      document.removeEventListener('keydown', handleEscape, false)
    }
  }, [handleEscape, isOpen])

  if (typeof window === 'undefined' || !target) return null

  const modalComponent = (
    <div className={classNames(containerClassName, 'fadeIn')}>
      <div
        className={styles.modal}
        onClick={(e) => {
          e.stopPropagation()
          if (e.target === e.currentTarget && !disableOutsideClick) onClose()
        }}
        data-testid={'modalContainer'}
      >
        <div className={styles.modalContainer}>
          {showCloseButton ? (
            <Panel
              className={classNames(styles.modalClose, showPagination ? styles.modalClosePaginated : null)}
              inverseTheme={false}
              layer={0}
              onClick={() => onClose()}
            >
              <Svg size={0.75} icon={'close'} data-testid={'modalCloseButton'} />
            </Panel>
          ) : null}
          {showPagination ? (
            <div className={styles.modalBodyContainer}>
              <div className={styles.paginationLeft}>
                <PaginationControl
                  direction={'left'}
                  intent={'action'}
                  onClick={() => {
                    if (!showPagination) return
                    onPaginationLeft()
                  }}
                />
              </div>
              <Panel
                className={classNames(styles.modalBody, styles.modalBodyPaginated)}
                inverseTheme={isInverseTheme}
                layer={0}
              >
                {children}
              </Panel>
              <div className={styles.paginationRight}>
                <PaginationControl
                  direction={'right'}
                  intent={'action'}
                  onClick={() => {
                    if (!showPagination) return
                    onPaginationRight()
                  }}
                />
              </div>
            </div>
          ) : (
            <Panel className={styles.modalBody} inverseTheme={isInverseTheme}>
              {children}
            </Panel>
          )}
        </div>
      </div>
      <Mask onClick={() => onClose()} visible={isOpen} />
    </div>
  )

  if (isOpen) {
    return createPortal(
      focusTrap === false ? modalComponent : <FocusTrap isActive={internalIsOpen}>{modalComponent}</FocusTrap>,
      target,
    )
  }

  return createPortal(null, target)
}

export default Modal
